import React from 'react';
import {Body} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import type {CampaignProps, CampaignPaidGigBriefItem} from '@/types/campaign';
import BriefField from '../../shared/BriefField';
import BriefItemHtml from '../../shared/BriefItemHtml';
import BriefSection from '../../shared/BriefSection';
import BriefTag from '../../shared/BriefTag';
import DividedStack from '../../shared/DividedStack';
import {
  getBriefItemByTitle,
  type SectionsToHide,
} from '../../utils/briefItemFilters';
import styles from './CreatorDetails.module.scss';

function formatFollowerCount(count: number): string {
  if (count >= 1000) {
    return `${Math.round(count / 1000)}K`;
  }
  return String(count);
}

interface CreatorDetailsProps {
  campaign: CampaignProps;
  briefItems: CampaignPaidGigBriefItem[];
  sectionsToHide: SectionsToHide;
}

function CreatorDetails({
  campaign,
  briefItems,
  sectionsToHide,
}: CreatorDetailsProps) {
  const {
    preferredCreatorGenders,
    preferredCreatorAgeDetail,
    preferredCreatorStates,
    preferredCreatorCountries,
    preferredCreatorCities,
    unlocksAt,
    locksAbove,
  } = campaign;

  const idealCreatorItem = getBriefItemByTitle(briefItems, 'Ideal creator');
  const hasFollowerCount =
    !sectionsToHide.followersCount && (unlocksAt != null || locksAbove != null);
  const hasGenders = (preferredCreatorGenders?.length ?? 0) > 0;
  const hasMinAge = preferredCreatorAgeDetail?.minAge != null;
  const hasMaxAge = preferredCreatorAgeDetail?.maxAge != null;
  const hasAge = hasMinAge || hasMaxAge;
  const allLocations = [
    ...(preferredCreatorCountries ?? []).map((c) => c.name),
    ...(preferredCreatorStates ?? []).map((s) => s.name),
    ...(preferredCreatorCities ?? []).map((c) => c.name),
  ].filter((n): n is string => n != null);
  const hasLocations = allLocations.length > 0;

  if (
    !hasFollowerCount &&
    !hasGenders &&
    !hasAge &&
    !hasLocations &&
    !idealCreatorItem?.contentHtml
  ) {
    return null;
  }

  const followersGenderRow =
    hasFollowerCount || hasGenders ? (
      <div className={styles.row}>
        {hasFollowerCount ? (
          <div className={styles.col}>
            <BriefField label={translate('Follower count')}>
              <Body size="xl">
                {unlocksAt != null ? formatFollowerCount(unlocksAt) : ''}
                {unlocksAt != null && locksAbove != null ? '–' : ''}
                {locksAbove != null ? formatFollowerCount(locksAbove) : ''}
              </Body>
            </BriefField>
          </div>
        ) : null}
        {hasGenders ? (
          <div className={styles.col}>
            <BriefField label={translate('Gender')}>
              <Body size="xl">
                {(preferredCreatorGenders ?? []).map((g) => g.name).join(', ')}
              </Body>
            </BriefField>
          </div>
        ) : null}
      </div>
    ) : null;

  const ageRow = hasAge ? (
    <div className={styles.row}>
      {hasMinAge ? (
        <div className={styles.col}>
          <BriefField label={translate('Minimum age')}>
            <Body size="xl">{preferredCreatorAgeDetail?.minAge}</Body>
          </BriefField>
        </div>
      ) : null}
      {hasMaxAge ? (
        <div className={styles.col}>
          <BriefField label={translate('Maximum age')}>
            <Body size="xl">{preferredCreatorAgeDetail?.maxAge}</Body>
          </BriefField>
        </div>
      ) : null}
    </div>
  ) : null;

  const locationRow = hasLocations ? (
    <BriefField label={translate('Location')}>
      <div className={styles.chips}>
        {allLocations.map((name) => (
          <BriefTag key={name} label={name} />
        ))}
      </div>
    </BriefField>
  ) : null;

  const idealCreatorRow = idealCreatorItem?.contentHtml ? (
    <BriefField label={translate('Ideal creator')}>
      <BriefItemHtml contentHtml={idealCreatorItem.contentHtml} />
    </BriefField>
  ) : null;

  return (
    <BriefSection title={translate('Creator details')}>
      <DividedStack
        items={[followersGenderRow, ageRow, locationRow, idealCreatorRow]}
      />
    </BriefSection>
  );
}

export default CreatorDetails;
